import {useState} from 'react'
import {useQuery, useMutation} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {useOpenRequestGuard} from '@/hooks/use-open-request-guard'
import {OpenRequestBanner} from '@/components/shared/OpenRequestBanner'
import {Sparkles, Play, CheckCircle, XCircle, AlertCircle, Users, Code, History, Pencil, Power, PowerOff, Activity, Clock} from 'lucide-react'
import {useNavigate, useParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {Button} from '@/components/ui/button'
import {Card, CardHeader, CardTitle, CardContent} from '@/components/ui/card'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {OwnersSection, OwnersEntityType} from '@/components/owners/OwnersSection'
import {useAppConfig} from '@/hooks/use-app-config'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '@/components/ui/alert'
import {useAuth} from '@/contexts/auth-context'
import {AutoAssignRuleExecutionStatus, type AutoAssignRuleRequest} from '@/types/auto-assign-rule'
import {RuleRequestType, TestResultAction} from '@/types/auto-assign-rule-request'
import type {RuleTest, AutoAssignRuleRequestCreateRequest} from '@/types/auto-assign-rule-request'
import {AffectedFilter, useAffectedFilterLabel, useTestResultActionLabel} from '@/features/governance/labels'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {permissions} from '@/lib/permissions'
import {formatTimestamp} from '@/lib/date-utils'
import {PageLayout} from '@/components/shared/PageLayout'
import {CardSection} from '@/components/shared/CardSection'
import {MutedText} from '@/components/shared/MutedText'
import {StatValue} from '@/components/shared/StatValue'
import {TextLink} from '@/components/shared/TextLink'
import {IconText} from '@/components/shared/IconText'
import {DetailPageGrid} from '@/components/shared/DetailPageGrid'
import {DetailList} from '@/components/shared/DetailList'
import {DetailField} from '@/components/shared/DetailField'
import {MutedStatBox} from '@/components/shared/MutedStatBox'
import {TimelineItem} from '@/components/shared/TimelineItem'
import {StatusText} from '@/components/shared/StatusText'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {EMPTY_SEARCH_AND_FILTERS, type SearchAndFilters} from '@/hooks/use-server-pagination'
import type {SearchAndFilterContract} from '@/types/endpoint-contract'
import {ScrollableBox} from '@/components/shared/ScrollableBox'
import {ImpactAnalysisDialog} from '@/components/auto-assign/ImpactAnalysisDialog'

const AFFECTED_SEARCH_CONTRACT: SearchAndFilterContract = {
    sort: {},
    filter: {},
    search: {param: 'q'},
}

export function AutoAssignRuleDetail() {
    const services = useServices()
    const i18n = useI18n()
    const affectedFilterLabel = useAffectedFilterLabel()
    const testResultActionLabel = useTestResultActionLabel()
    usePageTitle(i18n.autoAssignRules.detailPageTitle)
    const navigate = useNavigate()
    const {id} = useParams<{ id: string }>()
    const {hasPermission} = useAuth()

    // Test state
    const [testResults, setTestResults] = useState<RuleTest | null>(null)
    const [testError, setTestError] = useState<string | null>(null)

    // Affected identities dialog state
    const affectedDialog = useDialog()
    const [affectedFilter, setAffectedFilter] = useState<AffectedFilter>(AffectedFilter.ALL)
    const [affectedSearch, setAffectedSearch] = useState<SearchAndFilters>(EMPTY_SEARCH_AND_FILTERS)

    // Activate/Deactivate dialog state
    const activateDialog = useDialog()
    const deactivateDialog = useDialog()

    // Permissions
    const canTest = hasPermission(permissions.autoAssignRules.test)
    const canCreate = hasPermission(permissions.autoAssignRules.create)
    const canImpactAnalysis = hasPermission(permissions.autoAssignRules.impactAnalysis)
    const canUpdate = hasPermission(permissions.autoAssignRules.update)
    const {autoAssignRuleResponsibilityTypes} = useAppConfig()
    const [impactDialogOpen, setImpactDialogOpen] = useState(false)

    // Fetch rule
    const {data: rule, isLoading, error} = useQuery({
        queryKey: ['auto-assign-rule', id],
        queryFn: () => services.autoAssignRules.detail(id!),
        enabled: !!id,
    })

    // Fetch versions
    const {data: versions = []} = useQuery({
        queryKey: ['auto-assign-rule-versions', id],
        queryFn: () => services.autoAssignRules.versions(id!),
        enabled: !!id,
    })

    // Fetch profiles
    const {data: profiles = []} = useQuery({
        queryKey: ['auto-assign-rule-profiles', id],
        queryFn: () => services.autoAssignRules.profiles(id!),
        enabled: !!id,
    })

    // Fetch execution logs
    const {data: executionsPage} = useQuery({
        queryKey: ['auto-assign-rule-executions', id],

        queryFn: () => services.autoAssignRules.executions(id!, {size: 5}),
        enabled: !!id,
    })
    const executions = executionsPage?.content || []

    // Check for open (DRAFT or PENDING) requests for this rule
    const {openRequest, hasOpenRequest} = useOpenRequestGuard<AutoAssignRuleRequest>({
        queryKey: ['auto-assign-rule-open-request', id],
        queryFn: () => services.autoAssignRules.listRequests({ruleId: id!, status: ['DRAFT', 'PENDING'], size: 1}),
        enabled: !!id,
    })

    // Test mutation
    const testMutation = useMutation({
        mutationFn: () =>
            services.autoAssignRules.test(id!, {
                conditionExpr: rule?.conditionExpr,
                limit: 50,
            }),
        onSuccess: (data) => {
            setTestResults(data)
            setTestError(null)
        },
        onError: (err: Error) => {
            setTestError(err.message)
            setTestResults(null)
        },
    })

    // Create activate/deactivate request mutation
    const createRequestMutation = useEntityMutation({
        mutationFn: (data: AutoAssignRuleRequestCreateRequest) =>
            services.autoAssignRules.createRequest(data),
        invalidate: [['rule-requests'], ['auto-assign-rule', id]],
        successMessage: i18n.autoAssignRules.requestCreated,
        onSuccess: (request) => {
            activateDialog.close()
            deactivateDialog.close()
            navigate(routes.accessGovernance.autoAssignRuleRequests.detail(request.id))
        },
    })

    // Handle activate request creation
    const handleActivate = () => {
        if (!rule) return
        const proposedOwnerId = rule.owners[0]?.ownerPartyId
        if (!proposedOwnerId) return
        createRequestMutation.mutate({
            requestType: RuleRequestType.ACTIVATE,
            ruleId: rule.id,
            name: rule.name,
            description: rule.description || undefined,
            conditionExpr: rule.conditionExpr,
            priority: rule.priority,
            removalGraceDays: rule.removalGraceDays,
            owners: rule.owners.map(o => ({ownerPartyId: o.ownerPartyId, responsibility: o.responsibility})),
            userRisk: rule.userRisk || undefined,
            profileIds: profiles.map(p => p.accessProfile.id),
        })
    }

    // Handle deactivate request creation
    const handleDeactivate = () => {
        if (!rule) return
        const proposedOwnerId = rule.owners[0]?.ownerPartyId
        if (!proposedOwnerId) return
        createRequestMutation.mutate({
            requestType: RuleRequestType.DEACTIVATE,
            ruleId: rule.id,
            name: rule.name,
            description: rule.description || undefined,
            conditionExpr: rule.conditionExpr,
            priority: rule.priority,
            removalGraceDays: rule.removalGraceDays,
            owners: rule.owners.map(o => ({ownerPartyId: o.ownerPartyId, responsibility: o.responsibility})),
            userRisk: rule.userRisk || undefined,
            profileIds: profiles.map(p => p.accessProfile.id),
        })
    }

    // Handle edit - navigate to create page with rule data
    const handleEdit = () => {
        if (!rule) return
        // Navigate to create page with query params for pre-filling
        const params = new URLSearchParams({
            mode: 'update',
            ruleId: rule.id,
        })
        navigate(routes.accessGovernance.autoAssignRuleRequests.create(params.toString()))
    }

    // Get risk badge variant
    const getRiskBadgeVariant = (risk: number | null) => {
        if (risk === null) return BadgeVariant.OUTLINE
        if (risk <= 25) return BadgeVariant.SUCCESS
        if (risk <= 50) return BadgeVariant.SECONDARY
        if (risk <= 75) return BadgeVariant.WARNING
        return BadgeVariant.DESTRUCTIVE
    }

    // Filter test results
    const filteredAffectedIdentities = testResults?.items.filter(item => {
        if (affectedFilter !== AffectedFilter.ALL && item.action !== affectedFilter) return false
        if (affectedSearch.q) {
            const search = affectedSearch.q.toLowerCase()
            return item.identityDisplayName.toLowerCase().includes(search) ||
                item.partyDisplayName.toLowerCase().includes(search)
        }
        return true
    }) || []

    return (
        <PageLayout
            variant="detail"
            title={rule?.name ?? i18n.autoAssignRules.title}
            icon={CONCEPT_ICON.autoAssignRule}
            backTo={true}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(rule, 'AUTO_ASSIGN_RULE') : undefined)}
            data-testid="auto-assign-rule-detail-page"
            description={rule ? (
                <InlineRow>
                    {rule.active ? (
                        <Badge variant={BadgeVariant.SUCCESS} className="gap-1">
                            <CheckCircle className="h-3 w-3"/>
                            {i18n.common.active}
                        </Badge>
                    ) : (
                        <Badge variant={BadgeVariant.SECONDARY} className="gap-1">
                            <XCircle className="h-3 w-3"/>
                            {i18n.common.inactive}
                        </Badge>
                    )}
                    <Badge variant="outline">{i18n.common.priorityValue({value: rule.priority})}</Badge>
                    <Badge variant="secondary">{i18n.common.versionPrefix({version: rule.currentVersion})}</Badge>
                </InlineRow>
            ) : undefined}
            actions={rule ? (
                <InlineRow>
                    {canTest && (
                        <Button
                            data-testid="btn-test-rule"
                            variant="outline"
                            onClick={() => testMutation.mutate()}
                            disabled={testMutation.isPending}
                        >
                            <IconText icon={Play}>{testMutation.isPending ? i18n.autoAssignRules.testing : i18n.autoAssignRules.testRule}</IconText>
                        </Button>
                    )}
                    {canImpactAnalysis && (
                        <Button
                            data-testid="btn-impact-analysis"
                            variant="outline"
                            onClick={() => setImpactDialogOpen(true)}
                        >
                            <IconText icon={Sparkles}>{i18n.autoAssignRules.impactAnalysis.runButton}</IconText>
                        </Button>
                    )}
                    {canCreate && (
                        <>
                            <Button
                                data-testid="btn-edit"
                                variant="outline"
                                disabled={hasOpenRequest}
                                onClick={handleEdit}
                            >
                                <IconText icon={Pencil}>{i18n.common.edit}</IconText>
                            </Button>
                            {rule.active ? (
                                <Button
                                    data-testid="btn-deactivate"
                                    variant="outline"
                                    disabled={hasOpenRequest}
                                    onClick={deactivateDialog.open}
                                >
                                    <IconText icon={PowerOff}>{i18n.autoAssignRules.deactivateRule}</IconText>
                                </Button>
                            ) : (
                                <Button
                                    data-testid="btn-activate"
                                    variant="outline"
                                    disabled={hasOpenRequest}
                                    onClick={activateDialog.open}
                                >
                                    <IconText icon={Power}>{i18n.autoAssignRules.activateRule}</IconText>
                                </Button>
                            )}
                        </>
                    )}
                </InlineRow>
            ) : undefined}
        >
            {rule && <>
            {/* Open request guard banner */}
            {hasOpenRequest && openRequest && (
                <OpenRequestBanner
                    message={i18n.autoAssignRuleRequests.openRequestExists}
                    linkTo={routes.accessGovernance.autoAssignRuleRequests.detail(openRequest.id)}
                    requestName={openRequest.name}
                    testId="open-request-banner"
                    linkTestId="link-open-request"
                />
            )}
            <DetailPageGrid
                main={
                    <>
                        {/* Rule Details */}
                        <DetailsCard
                            data-testid="card-rule-details"
                            title={i18n.autoAssignRules.ruleDetails}
                            description={i18n.autoAssignRules.ruleDetailsDesc}
                            fields={[
                                ...(rule.description ? [{label: i18n.common.description, value: rule.description, fullWidth: true as const}] : []),
                                {label: i18n.autoAssignRules.priority, value: String(rule.priority)},
                                {label: i18n.autoAssignRules.removalGraceDays, value: i18n.autoAssignRules.removalGraceDaysValue({days: rule.removalGraceDays})},
                                ...(rule.userRisk !== null ? [{label: i18n.autoAssignRules.userRiskScore, value: <Badge variant={getRiskBadgeVariant(rule.userRisk!)}>{rule.userRisk}</Badge>}] : []),
                            ]}
                        />

                        <OwnersSection
                            entityType={OwnersEntityType.autoAssignRules}
                            entityId={id!}
                            canEdit={canUpdate}
                            responsibilityOptions={autoAssignRuleResponsibilityTypes}
                        />

                        {/* Condition Expression */}
                        <CardSection data-testid="section-condition-expression" icon={Code} title={String(i18n.autoAssignRules.conditionExpression)} description={String(i18n.autoAssignRules.conditionExpressionDesc)}>
                            <CodeBlock>
                                {rule.conditionExpr}
                            </CodeBlock>
                        </CardSection>

                        {/* Access Profiles */}
                        <CardSection data-testid="section-access-profiles" icon={Users} title={String(i18n.autoAssignRules.accessProfiles)} description={String(i18n.autoAssignRules.accessProfilesCount({count: profiles.length}))}>
                            {profiles.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead data-testid="col-access-profile">{i18n.autoAssignRules.accessProfile}</TableHead>
                                            <TableHead data-testid="col-resource">{i18n.accessProfiles.resource}</TableHead>
                                            <TableHead data-testid="col-system">{i18n.accessProfiles.system}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {profiles.map((profile) => (
                                            <TableRow key={profile.id}>
                                                <TableCell>
                                                    <TextLink
                                                        testId={`link-access-profile-${profile.accessProfile.id}`}
                                                        to={routes.accessGovernance.profiles.detail(profile.accessProfile.id)}
                                                        variant="bold"
                                                    >
                                                        {profile.accessProfile.displayName}
                                                    </TextLink>
                                                </TableCell>
                                                <TableCell>{profile.resource.displayName}</TableCell>
                                                <TableCell>{profile.system.displayName}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <MutedText block centered className="py-4">
                                    {i18n.autoAssignRules.noAccessProfiles}
                                </MutedText>
                            )}
                        </CardSection>

                        {/* Test Results */}
                        {(testResults || testError) && (
                            <Card>
                                <CardHeader>
                                    <CardTitle data-testid="card-title-test-results">
                                        <IconText icon={Users} size="md">{i18n.autoAssignRules.testResults}</IconText>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {testError && (
                                        <Alert variant="destructive">
                                            <AlertCircle className="h-4 w-4"/>
                                            <AlertTitle>{i18n.autoAssignRules.testFailed}</AlertTitle>
                                            <AlertDescription>{testError}</AlertDescription>
                                        </Alert>
                                    )}
                                    {testResults && (
                                        <Stack>
                                            <InlineRow gap="xl" wrap align="stretch">
                                                <MutedStatBox>
                                                    <MutedText size="sm">{i18n.autoAssignRules.evaluated}</MutedText>
                                                    <StatValue value={testResults.totalEvaluated}/>
                                                </MutedStatBox>
                                                <MutedStatBox>
                                                    <MutedText size="sm">{i18n.autoAssignRules.matched}</MutedText>
                                                    <StatValue variant="primary" value={testResults.matched}/>
                                                </MutedStatBox>
                                                <MutedStatBox variant="success">
                                                    <MutedText size="sm">{i18n.autoAssignRules.toAdd}</MutedText>
                                                    <StatValue value={testResults.toAdd} variant="success"/>
                                                </MutedStatBox>
                                                <MutedStatBox variant="danger">
                                                    <MutedText size="sm">{i18n.autoAssignRules.toRemove}</MutedText>
                                                    <StatValue value={testResults.toRemove} variant="danger"/>
                                                </MutedStatBox>
                                            </InlineRow>
                                            {testResults.items.length > 0 && (
                                                <Button
                                                    data-testid="btn-view-affected"
                                                    variant="outline"
                                                    onClick={affectedDialog.open}
                                                >
                                                    {i18n.autoAssignRules.viewAffectedIdentities({count: testResults.items.length})}
                                                </Button>
                                            )}
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </>
                }
                sidebar={
                    <Stack gap="lg">
                        {/* Metadata */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base" data-testid="card-title-rule-information">{i18n.autoAssignRules.ruleInformation}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <DetailList>
                                    <DetailField label={i18n.autoAssignRules.profileCount} value={rule.profileCount}/>
                                    <DetailField label={i18n.common.createdAt} value={formatTimestamp(rule.createdAt)}/>
                                    <DetailField label={i18n.common.lastUpdated} value={formatTimestamp(rule.updatedAt)}/>
                                </DetailList>
                            </CardContent>
                        </Card>

                        {/* Version History */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base" data-testid="card-title-version-history">
                                    <IconText icon={History} size="sm">
                                        {i18n.autoAssignRules.versionHistory}
                                    </IconText>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {versions.length > 0 ? (
                                    <Stack gap="sm">
                                        {versions.map((version) => (
                                            <TimelineItem key={version.id}>
                                                <InlineRow gap="md">
                                                    <Badge variant="outline" className="text-xs">
                                                        {i18n.common.versionPrefix({version: version.versionNumber})}
                                                    </Badge>
                                                    {version.versionNumber === rule.currentVersion && (
                                                        <Badge variant={BadgeVariant.SUCCESS}
                                                               className="text-xs">{i18n.autoAssignRules.current}</Badge>
                                                    )}
                                                </InlineRow>
                                                <MutedText size="xs" block className="mt-1">
                                                    {formatTimestamp(version.createdAt)}
                                                    {version.authoredBy && i18n.autoAssignRules.versionByAuthor({name: version.authoredBy})}
                                                </MutedText>
                                            </TimelineItem>
                                        ))}
                                    </Stack>
                                ) : (
                                    <MutedText size="sm">{i18n.autoAssignRules.noVersionHistory}</MutedText>
                                )}
                            </CardContent>
                        </Card>

                        {/* Execution History */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base" data-testid="card-title-execution-history">
                                    <IconText icon={Activity} size="sm">
                                        {i18n.autoAssignRules.executionHistory}
                                    </IconText>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {executions.length > 0 ? (
                                    <Stack gap="sm">
                                        {executions.map((exec) => (
                                            <TimelineItem key={exec.id}>
                                                <InlineRow gap="md">
                                                    <Badge
                                                        variant={
                                                            exec.status === AutoAssignRuleExecutionStatus.SUCCESS ? BadgeVariant.SUCCESS :
                                                                exec.status === AutoAssignRuleExecutionStatus.PARTIAL ? BadgeVariant.WARNING :
                                                                    BadgeVariant.DESTRUCTIVE
                                                        }
                                                        className="text-xs"
                                                    >
                                                        {exec.status}
                                                    </Badge>
                                                    <Badge variant="outline" className="text-xs">
                                                        {i18n.common.versionPrefix({version: exec.ruleVersion})}
                                                    </Badge>
                                                </InlineRow>
                                                <InlineRow gap="md" className="mt-1">
                                                    <IconText icon={Clock} size="sm">
                                                        <MutedText size="xs">{formatTimestamp(exec.executedAt)}</MutedText>
                                                    </IconText>
                                                </InlineRow>
                                                <InlineRow gap="md" className="mt-1 text-xs">
                                                    <MutedText size="xs">
                                                        {i18n.common.matched} <span>{exec.identitiesMatched}</span>
                                                    </MutedText>
                                                    <StatusText tone="success">+{exec.assignmentsAdded}</StatusText>
                                                    <StatusText tone="danger">-{exec.removalsScheduled}</StatusText>
                                                </InlineRow>
                                                {exec.status !== AutoAssignRuleExecutionStatus.SUCCESS && (
                                                    <InlineRow gap="md" className="mt-1 text-xs">
                                                        <TextLink
                                                            testId={`execution-error-link-${exec.id}`}
                                                            to={routes.admin.errorLogs.byCorrelation(exec.id)}
                                                        >
                                                            {i18n.common.viewErrors}
                                                        </TextLink>
                                                    </InlineRow>
                                                )}
                                            </TimelineItem>
                                        ))}
                                        {executionsPage && executionsPage.totalElements > 5 && (
                                            <TextLink
                                                testId="link-view-all-executions"
                                                to={routes.accessGovernance.rules.executionsForRule(id!)}
                                            >
                                                <MutedText size="xs">{i18n.common.viewAllExecutions({count: executionsPage.totalElements})}</MutedText>
                                            </TextLink>
                                        )}
                                    </Stack>
                                ) : (
                                    <MutedText size="sm">{i18n.autoAssignRules.noExecutionsYet}</MutedText>
                                )}
                            </CardContent>
                        </Card>
                    </Stack>
                }
            />

            {/* Affected Identities Dialog */}
            <Dialog open={affectedDialog.isOpen} onOpenChange={affectedDialog.bind.onOpenChange}>
                <DialogContent className="max-w-4xl max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle>{i18n.autoAssignRules.affectedIdentities}</DialogTitle>
                        <DialogDescription>
                            {i18n.autoAssignRules.affectedIdentitiesDesc}
                        </DialogDescription>
                    </DialogHeader>
                    <Stack>
                        {/* Filters */}
                        <InlineRow gap="md" wrap>
                            <UnifiedSearchBar
                                contract={AFFECTED_SEARCH_CONTRACT}
                                value={affectedSearch}
                                onChange={(next) => setAffectedSearch(prev => ({...prev, ...next}))}
                                placeholder={i18n.autoAssignRules.searchIdentities as string}
                                testIdNamespace="affected-identities"
                            />
                            <InlineRow gap="xs">
                                {([AffectedFilter.ALL, AffectedFilter.ADD, AffectedFilter.REMOVE, AffectedFilter.UNCHANGED] as const).map((filter) => (
                                    <Button
                                        key={filter}
                                        data-testid={`btn-filter-affected-${filter}`}
                                        variant={affectedFilter === filter ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => setAffectedFilter(filter)}
                                    >
                                        {affectedFilterLabel(filter)}
                                    </Button>
                                ))}
                            </InlineRow>
                        </InlineRow>

                        {/* Results Table */}
                        <ScrollableBox maxHeight="lg">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead data-testid="col-identity">{i18n.autoAssignRules.identity}</TableHead>
                                        <TableHead data-testid="col-party">{i18n.autoAssignRules.party}</TableHead>
                                        <TableHead data-testid="col-action">{i18n.autoAssignRules.action}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredAffectedIdentities.map((item, index) => (
                                        <TableRow key={`${item.identityId}-${index}`}>
                                            <TableCell>
                                                <TextLink
                                                    testId={`link-affected-identity-${item.identityId}`}
                                                    to={routes.identityManagement.identities.detail(item.identityId)}
                                                >
                                                    {item.identityDisplayName}
                                                </TextLink>
                                            </TableCell>
                                            <TableCell>
                                                <TextLink
                                                    testId={`link-affected-party-${item.partyId}`}
                                                    to={routes.identityManagement.parties.detail(item.partyId)}
                                                >
                                                    {item.partyDisplayName}
                                                </TextLink>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={
                                                        item.action === TestResultAction.ADD ? BadgeVariant.SUCCESS :
                                                            item.action === TestResultAction.REMOVE ? BadgeVariant.DESTRUCTIVE :
                                                                BadgeVariant.SECONDARY
                                                    }
                                                >
                                                    {testResultActionLabel(item.action)}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredAffectedIdentities.length === 0 && (
                                        <TableEmptyState
                                            colSpan={3}
                                            hasFilters={false}
                                            matchMessage={i18n.autoAssignRules.noIdentitiesMatch}
                                            emptyMessage={i18n.autoAssignRules.noIdentitiesMatch}
                                        />
                                    )}
                                </TableBody>
                            </Table>
                        </ScrollableBox>
                    </Stack>
                </DialogContent>
            </Dialog>

            {/* Activate Confirmation Dialog */}
            <Dialog open={activateDialog.isOpen} onOpenChange={activateDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.autoAssignRules.activateRule}</DialogTitle>
                        <DialogDescription>
                            {i18n.autoAssignRules.activateRuleDesc({name: rule.name})}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="activate"
                        onCancel={activateDialog.close}
                        saving={createRequestMutation.isPending}
                        saveLabel={i18n.autoAssignRules.createActivateRequest}
                        savingLabel={i18n.autoAssignRules.creating}
                        saveType="button"
                        onSave={handleActivate}
                    />
                </DialogContent>
            </Dialog>

            {/* Deactivate Confirmation Dialog */}
            <Dialog open={deactivateDialog.isOpen} onOpenChange={deactivateDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.autoAssignRules.deactivateRule}</DialogTitle>
                        <DialogDescription>
                            {i18n.autoAssignRules.deactivateRuleDesc({name: rule.name})}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="deactivate"
                        onCancel={deactivateDialog.close}
                        saving={createRequestMutation.isPending}
                        saveLabel={i18n.autoAssignRules.createDeactivateRequest}
                        savingLabel={i18n.autoAssignRules.creating}
                        destructive
                        saveType="button"
                        onSave={handleDeactivate}
                    />
                </DialogContent>
            </Dialog>
        </>}
        {rule && canImpactAnalysis && (
            <ImpactAnalysisDialog
                ruleId={rule.id}
                open={impactDialogOpen}
                onOpenChange={setImpactDialogOpen}
            />
        )}
        </PageLayout>
    )
}
